import React, { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  DirectionsCar,
  TwoWheeler,
  CurrencyRupee,
  ConfirmationNumber,
  Star,
  CalendarToday,
  ShowChart,
  Assessment,
  TrendingUp,
  Speed,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FlexRow,
  LabelText,
  MetricChip,
  MetricCard,
} from "../../components/common";
import HeroSection from "../../components/HeroSection";
import ScrollToTopFab from "../../components/ScrollToTopFab";
import { TableColumn } from "../../components/DataTable";
import DataTableCard from "../../components/DataTableCard";
import PageToolbar from "../../components/Toolbar";
import FeedbackRatingCard from "../../components/FeedbackRatingCard";
import ViewTabs from "../../components/ViewTabs";

import { useAppSelector, useAppDispatch } from "../../store/store";
import { useErrorToast } from "../../hooks";

import {
  useGetDailyAnalyticsQuery,
  useGetMonthlyAnalyticsQuery,
  useGetYearlyAnalyticsQuery,
} from "../../features/analytics/analyticsApiSlice";
import { selectCurrentUser } from "../../store/slices/authSlice";
import {
  setDailyCache,
  setMonthlyCache,
  setYearlyCache,
} from "../../store/slices/analyticsSlice";
import { format } from "date-fns";

import { useAnalyticsDateParams } from "../../utils/dateFilterUtils";
import { gradients } from "../../utils/theme";
import { DailyAnalytics, MonthlyAnalytics, YearlyAnalytics } from "../../types";

/**
 * Dashboard Component - MallInHand Premium Analytics
 *
 * Premium parking analytics dashboard for mall administrators.
 * Features luxury aesthetics with deep navy & gold theme.
 *
 * Features:
 * - Real-time analytics with framer-motion animations
 * - Vehicle breakdown (cars vs bikes) with revenue tracking
 * - Feedback metrics with premium visualizations
 * - Glass-morphism card effects with smooth transitions
 * - Mobile-first responsive design
 * - Premium skeleton loading states
 * - Enterprise-grade UI/UX trusted by luxury malls
 */
const Dashboard: React.FC = () => {
  const userDetails = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<number>(0);

  // Use scroll-to-top hook for button visibility and scroll functionality

  // Use global date filter parameters (startDate/endDate format)
  const dateParams = useAnalyticsDateParams();
  // venueId is extracted from JWT token by backend automatically

  // Fetch data based on active tab
  const {
    data: dailyData,
    isLoading: isDailyLoading,
    isFetching: isDailyFetching,
    isError: isDailyError,
  } = useGetDailyAnalyticsQuery({ ...dateParams }, { skip: activeTab !== 0 });

  const {
    data: monthlyData,
    isLoading: isMonthlyLoading,
    isFetching: isMonthlyFetching,
    isError: isMonthlyError,
  } = useGetMonthlyAnalyticsQuery({ ...dateParams }, { skip: activeTab !== 1 });

  const {
    data: yearlyData,
    isLoading: isYearlyLoading,
    isFetching: isYearlyFetching,
    isError: isYearlyError,
  } = useGetYearlyAnalyticsQuery({ ...dateParams }, { skip: activeTab !== 2 });

  // Determine current state
  const isLoading =
    (activeTab === 0 && isDailyLoading) ||
    (activeTab === 1 && isMonthlyLoading) ||
    (activeTab === 2 && isYearlyLoading);

  const isFetching =
    (activeTab === 0 && isDailyFetching) ||
    (activeTab === 1 && isMonthlyFetching) ||
    (activeTab === 2 && isYearlyFetching);

  const currentData =
    activeTab === 0 ? dailyData : activeTab === 1 ? monthlyData : yearlyData;

  // Get current error based on active tab
  const currentIsError =
    activeTab === 0
      ? isDailyError
      : activeTab === 1
        ? isMonthlyError
        : activeTab === 2
          ? isYearlyError
          : false;

  // Cache analytics data in Redux when fetched
  useEffect(() => {
    if (dailyData?.data && activeTab === 0) {
      dispatch(
        setDailyCache({
          data: dailyData.data,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
        }),
      );
    }
  }, [
    dailyData,
    activeTab,
    dateParams.startDate,
    dateParams.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (monthlyData?.data && activeTab === 1) {
      dispatch(
        setMonthlyCache({
          data: monthlyData.data,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
        }),
      );
    }
  }, [
    monthlyData,
    activeTab,
    dateParams.startDate,
    dateParams.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (yearlyData?.data && activeTab === 2) {
      dispatch(
        setYearlyCache({
          data: yearlyData.data,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
        }),
      );
    }
  }, [
    yearlyData,
    activeTab,
    dateParams.startDate,
    dateParams.endDate,
    dispatch,
  ]);

  // Calculate aggregated metrics from first item (all items have same totals)
  const getAggregatedMetrics = () => {
    // Return default values if no data
    if (!currentData || !currentData.data || currentData.data.length === 0) {
      return {
        totalTickets: 0,
        totalRevenue: 0,
        carCount: 0,
        bikeCount: 0,
        carRevenue: 0,
        bikeRevenue: 0,
        feedbackMetrics: {
          totalMallFeedback: 0,
          totalParkingFeedback: 0,
          averageMallRating: 0,
          averageParkingRating: 0,
          mallRatingBreakdown: {
            excellent: 0,
            good: 0,
            average: 0,
            poor: 0,
            terrible: 0,
          },
          parkingRatingBreakdown: {
            excellent: 0,
            good: 0,
            average: 0,
            poor: 0,
            terrible: 0,
          },
        },
      };
    }

    const firstItem = currentData.data[0];
    const totalTickets = firstItem.totalTickets;
    const totalRevenue = firstItem.totalRevenue;

    // Safely access vehicleBreakdown
    const vehicleBreakdown = firstItem.vehicleBreakdown;
    const carData = vehicleBreakdown["Car"] || { count: 0, revenue: 0 };
    const bikeData = vehicleBreakdown["Bike"] || { count: 0, revenue: 0 };

    const carCount = typeof carData === "object" ? carData.count : 0;
    const bikeCount = typeof bikeData === "object" ? bikeData.count : 0;
    const carRevenue = typeof carData === "object" ? carData.revenue : 0;
    const bikeRevenue = typeof bikeData === "object" ? bikeData.revenue : 0;

    return {
      totalTickets,
      totalRevenue,
      carCount,
      bikeCount,
      carRevenue,
      bikeRevenue,
      feedbackMetrics: firstItem.feedbackMetrics,
    };
  };

  const metrics = getAggregatedMetrics();

  // Define table columns for daily data
  const dailyStatsColumns: TableColumn<DailyAnalytics>[] = [
    {
      key: "date",
      label: "Date",
      render: (_, item) => (
        <FlexRow gap={1}>
          <CalendarToday fontSize="small" sx={{ color: "primary.main" }} />
          <LabelText>{format(new Date(item.date), "MMM dd, yyyy")}</LabelText>
        </FlexRow>
      ),
    },
    {
      key: "totalTickets",
      label: "Total Tickets",
      render: (_, item) => (
        <MetricChip type="ticket" value={item.totalTickets.toLocaleString()} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Cars",
      render: (_, item) => (
        <MetricChip type="car" value={item.vehicleBreakdown.Car.count} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Bikes",
      render: (_, item) => (
        <MetricChip type="bike" value={item.vehicleBreakdown.Bike.count} />
      ),
    },
    {
      key: "totalRevenue",
      label: "Revenue",
      render: (_, item) => (
        <MetricChip
          type="revenue"
          value={`${item.totalRevenue.toLocaleString()}`}
        />
      ),
    },
  ];

  // Define table columns for monthly data
  const monthlyStatsColumns: TableColumn<MonthlyAnalytics>[] = [
    {
      key: "month",
      label: "Month",
      render: (_, item) => (
        <FlexRow gap={1}>
          <CalendarToday fontSize="small" sx={{ color: "primary.main" }} />
          <LabelText>{format(new Date(item.month), "MMMM yyyy")}</LabelText>
        </FlexRow>
      ),
    },
    {
      key: "totalTickets",
      label: "Total Tickets",
      render: (_, item) => (
        <MetricChip type="ticket" value={item.totalTickets.toLocaleString()} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Cars",
      render: (_, item) => (
        <MetricChip type="car" value={item.vehicleBreakdown.Car?.count || 0} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Bikes",
      render: (_, item) => (
        <MetricChip
          type="bike"
          value={item.vehicleBreakdown.Bike?.count || 0}
        />
      ),
    },
    {
      key: "totalRevenue",
      label: "Revenue",
      render: (_, item) => (
        <MetricChip
          type="revenue"
          value={`₹${item.totalRevenue.toLocaleString()}`}
        />
      ),
    },
    {
      key: "daysWithData",
      label: "Active Days",
      render: (_, item) => (
        <MetricChip type="activity" value={`${item.daysWithData} days`} />
      ),
    },
  ];

  // Define table columns for yearly data
  const yearlyStatsColumns: TableColumn<YearlyAnalytics>[] = [
    {
      key: "year",
      label: "Year",
      render: (_, item) => (
        <FlexRow gap={1}>
          <CalendarToday fontSize="small" sx={{ color: "primary.main" }} />
          <LabelText>{item.year}</LabelText>
        </FlexRow>
      ),
    },
    {
      key: "totalTickets",
      label: "Total Tickets",
      render: (_, item) => (
        <MetricChip type="ticket" value={item.totalTickets.toLocaleString()} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Cars",
      render: (_, item) => (
        <MetricChip type="car" value={item.vehicleBreakdown.Car?.count || 0} />
      ),
    },
    {
      key: "vehicleBreakdown",
      label: "Bikes",
      render: (_, item) => (
        <MetricChip
          type="bike"
          value={item.vehicleBreakdown.Bike?.count || 0}
        />
      ),
    },
    {
      key: "totalRevenue",
      label: "Revenue",
      render: (_, item) => (
        <MetricChip
          type="revenue"
          value={`${item.totalRevenue.toLocaleString()}`}
        />
      ),
    },
    {
      key: "monthsWithData",
      label: "Active Months",
      render: (_, item) => (
        <MetricChip type="activity" value={`${item.monthsWithData} months`} />
      ),
    },
  ];

  const handleViewChange = (view: "daily" | "monthly" | "yearly") => {
    const tabIndex = view === "daily" ? 0 : view === "monthly" ? 1 : 2;
    setActiveTab(tabIndex);
  };

  // Handle error toast notifications with automatic deduplication and mount delay
  const viewName =
    activeTab === 0 ? "daily" : activeTab === 1 ? "monthly" : "yearly";
  useErrorToast({
    isError: currentIsError,
    isFetching,
    isLoading,
    errorMessage: `Failed to load ${viewName} analytics data. Please try again.`,
    errorKey: `${viewName}-${activeTab}`,
    resetDependencies: [activeTab],
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: gradients.background,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Sticky Toolbar */}
      <PageToolbar
        viewContext={
          activeTab === 0 ? "daily" : activeTab === 1 ? "monthly" : "yearly"
        }
        onViewChange={handleViewChange}
      />

      {/* Hero Section with Premium Branding */}
      <HeroSection
        title="Performance Dashboard"
        subtitle={`Welcome back, ${
          userDetails?.displayName || "User"
        }! Track parking operations, visitor satisfaction, and revenue performance`}
        gradient="navyLux"
      >
        {/* View Selection Tabs in Hero */}
        <ViewTabs
          value={activeTab}
          onChange={(_, newValue: number) => setActiveTab(newValue)}
          tabs={[
            {
              value: 0,
              label: "Daily",
              icon: <CalendarToday />,
            },
            {
              value: 1,
              label: "Monthly",
              icon: <ShowChart />,
            },
            {
              value: 2,
              label: "Yearly",
              icon: <Assessment />,
            },
          ]}
        />
      </HeroSection>

      {/* Main Content - Premium Container */}
      <Box
        sx={{
          py: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 2, md: 1.5 },
          position: "relative",
        }}
      >
        {/* Content Wrapper with Animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Box sx={{ position: "relative", minHeight: 600 }}>
            {/* Premium Metrics Section Header */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                    boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                  }}
                >
                  <Speed sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      letterSpacing: "-0.5px",
                      fontSize: { xs: "1.2rem", md: "1.4rem" },
                      mb: 0.5,
                    }}
                  >
                    Parking & Revenue Overview
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    Real-time parking occupancy and revenue tracking across your
                    mall
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Vehicle & Revenue Breakdown */}
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
              {/* Total Tickets Card */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{ height: "100%" }}
                >
                  <MetricCard
                    icon={ConfirmationNumber}
                    label="Tickets"
                    value={metrics.totalTickets}
                    subtitle="Total parking tickets"
                    gradient={gradients.royalBlue}
                    variant="white"
                    showHeader={true}
                    breakdownItems={[
                      { label: "Cars", value: metrics.carCount },
                      { label: "Bikes", value: metrics.bikeCount },
                    ]}
                    chipLabel="All Vehicles"
                    chipColor="#1a237e"
                    borderColor="rgba(26,35,126,0.2)"
                    isLoading={isLoading || isFetching}
                  />
                </motion.div>
              </Grid>

              {/* Total Revenue Card */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  style={{ height: "100%" }}
                >
                  <MetricCard
                    icon={CurrencyRupee}
                    label="Revenue"
                    value={`${metrics.totalRevenue.toLocaleString()}`}
                    subtitle="Total earnings today"
                    gradient={gradients.gold}
                    variant="white"
                    showHeader={true}
                    breakdownItems={[
                      {
                        label: "Cars",
                        value: `₹${metrics.carRevenue.toLocaleString()}`,
                      },
                      {
                        label: "Bikes",
                        value: `₹${metrics.bikeRevenue.toLocaleString()}`,
                      },
                    ]}
                    chipLabel="Combined"
                    chipColor="#10b981"
                    borderColor="rgba(250,112,154,0.3)"
                    isLoading={isLoading || isFetching}
                  />
                </motion.div>
              </Grid>

              {/* Cars Revenue Card */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  style={{ height: "100%" }}
                >
                  <MetricCard
                    icon={DirectionsCar}
                    label="Cars"
                    value={metrics.carCount}
                    subtitle="Four-wheelers parked"
                    gradient={gradients.emerald}
                    variant="white"
                    showHeader={true}
                    breakdownItems={[
                      {
                        label: "Revenue",
                        value: `₹${metrics.carRevenue.toLocaleString()}`,
                      },
                    ]}
                    borderColor="rgba(15,157,88,0.2)"
                    isLoading={isLoading || isFetching}
                  />
                </motion.div>
              </Grid>

              {/* Bike Revenue Card */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  style={{ height: "100%" }}
                >
                  <MetricCard
                    icon={TwoWheeler}
                    label="Bikes"
                    value={metrics.bikeCount}
                    subtitle="Two-wheelers parked"
                    gradient={gradients.deepPurple}
                    variant="white"
                    showHeader={true}
                    breakdownItems={[
                      {
                        label: "Revenue",
                        value: `₹${metrics.bikeRevenue.toLocaleString()}`,
                      },
                    ]}
                    borderColor="rgba(74,20,140,0.2)"
                    isLoading={isLoading || isFetching}
                  />
                </motion.div>
              </Grid>
            </Grid>

            {/* Feedback Metrics Section */}
            <Box sx={{ mb: 4, mt: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background:
                      "linear-gradient(135deg, #d4af37 0%, #FFD700 100%)",
                    boxShadow: "0 6px 16px rgba(212, 175, 55, 0.3)",
                  }}
                >
                  <Star sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      letterSpacing: "-0.5px",
                      fontSize: { xs: "1.2rem", md: "1.4rem" },
                      mb: 0.5,
                    }}
                  >
                    Visitor Experience Ratings
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    Real-time feedback on mall shopping experience and parking
                    convenience
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
              {/* Mall Rating */}
              <Grid item xs={12} md={6}>
                <FeedbackRatingCard
                  title="Mall Experience"
                  icon={Star}
                  iconGradient="linear-gradient(135deg, #d4af37 0%, #FFD700 100%)"
                  iconShadow="0 4px 16px rgba(212,175,55,0.35)"
                  totalFeedback={metrics.feedbackMetrics.totalMallFeedback}
                  totalTickets={metrics.totalTickets}
                  averageRating={metrics.feedbackMetrics.averageMallRating}
                  ratingBreakdown={metrics.feedbackMetrics.mallRatingBreakdown}
                  chipBgColor="rgba(212,175,55,0.12)"
                  chipTextColor="#d4af37"
                  chipBorderColor="rgba(212,175,55,0.25)"
                  ratingGradient="linear-gradient(135deg, #d4af37 0%, #FFD700 100%)"
                  ratingBoxGradient="linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(255,215,0,0.12) 100%)"
                  ratingBoxBorderColor="rgba(212,175,55,0.25)"
                  hoverBorderColor="rgba(212,175,55,0.3)"
                  isLoading={isLoading || isFetching}
                />
              </Grid>

              {/* Parking Rating */}
              <Grid item xs={12} md={6}>
                <FeedbackRatingCard
                  title="Parking Experience"
                  icon={DirectionsCar}
                  iconGradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                  iconShadow="0 4px 16px rgba(26,35,126,0.3)"
                  totalFeedback={metrics.feedbackMetrics.totalParkingFeedback}
                  totalTickets={metrics.totalTickets}
                  averageRating={metrics.feedbackMetrics.averageParkingRating}
                  ratingBreakdown={
                    metrics.feedbackMetrics.parkingRatingBreakdown
                  }
                  chipBgColor="rgba(26,35,126,0.12)"
                  chipTextColor="#1a237e"
                  chipBorderColor="rgba(26,35,126,0.25)"
                  ratingGradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                  ratingBoxGradient="linear-gradient(135deg, rgba(26,35,126,0.1) 0%, rgba(57,73,171,0.12) 100%)"
                  ratingBoxBorderColor="rgba(26,35,126,0.25)"
                  hoverBorderColor="rgba(26,35,126,0.3)"
                  isLoading={isLoading || isFetching}
                />
              </Grid>
            </Grid>

            {/* Statistics Section Header */}
            <Box sx={{ mb: 4, mt: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                    boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                  }}
                >
                  <TrendingUp sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      letterSpacing: "-0.5px",
                      fontSize: { xs: "1.2rem", md: "1.4rem" },
                      mb: 0.5,
                    }}
                  >
                    {activeTab === 0
                      ? "Daily Performance"
                      : activeTab === 1
                        ? "Monthly Performance"
                        : "Yearly Performance"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    Comprehensive performance data for parking operations and
                    visitor feedback
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Data Table with Animation */}
            <AnimatePresence mode="wait">
              {activeTab === 0 && (
                <motion.div
                  key="daily"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <DataTableCard
                    title="Daily Statistics"
                    icon={Assessment}
                    showIconBox={true}
                    data={(currentData?.data as DailyAnalytics[]) || []}
                    columns={dailyStatsColumns}
                    countChip="days"
                    emptyState={{
                      icon: <Assessment sx={{ fontSize: 48, opacity: 0.5 }} />,
                      title: "No daily analytics available",
                      subtitle:
                        "Select a different date range to view daily parking statistics",
                    }}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    showTableOnEmpty={true}
                  />
                </motion.div>
              )}
              {activeTab === 1 && (
                <motion.div
                  key="monthly"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <DataTableCard
                    title="Monthly Statistics"
                    icon={Assessment}
                    showIconBox={true}
                    data={(currentData?.data as MonthlyAnalytics[]) || []}
                    columns={monthlyStatsColumns}
                    countChip="months"
                    emptyState={{
                      icon: <Assessment sx={{ fontSize: 48, opacity: 0.5 }} />,
                      title: "No monthly analytics available",
                      subtitle:
                        "Select a different date range to view monthly parking statistics",
                    }}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    showTableOnEmpty={true}
                  />
                </motion.div>
              )}
              {activeTab === 2 && (
                <motion.div
                  key="yearly"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <DataTableCard
                    title="Yearly Statistics"
                    icon={Assessment}
                    showIconBox={true}
                    data={(currentData?.data as YearlyAnalytics[]) || []}
                    columns={yearlyStatsColumns}
                    countChip="years"
                    emptyState={{
                      icon: <Assessment sx={{ fontSize: 48, opacity: 0.5 }} />,
                      title: "No yearly analytics available",
                      subtitle:
                        "Select a different date range to view yearly parking statistics",
                    }}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    showTableOnEmpty={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </motion.div>
      </Box>

      {/* Scroll to Top Button */}
      <ScrollToTopFab />
    </Box>
  );
};

export default React.memo(Dashboard);
